import { useState } from 'react'
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react'

export interface StructuredListField {
  key: string
  label: string
  type: 'string' | 'number' | 'boolean'
  default?: unknown
}

export interface StructuredListEditorProps {
  value: Array<Record<string, unknown>>
  onChange: (value: Array<Record<string, unknown>>) => void
  schema: StructuredListField[]
  disabled?: boolean
}

export function StructuredListEditor({ value, onChange, schema, disabled = false }: StructuredListEditorProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  const handleAdd = () => {
    const newItem: Record<string, unknown> = {}
    schema.forEach(field => {
      newItem[field.key] = field.default ?? (field.type === 'boolean' ? false : field.type === 'number' ? 0 : '')
    })
    onChange([...value, newItem])
    setEditingIndex(value.length)
  }

  const handleRemove = (index: number) => {
    const newValue = value.filter((_, i) => i !== index)
    onChange(newValue)
    if (editingIndex === index) {
      setEditingIndex(null)
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1)
    }
  }

  const handleMoveUp = (index: number) => {
    if (index === 0) return
    const newValue = [...value]
    const temp = newValue[index]!
    newValue[index] = newValue[index - 1]!
    newValue[index - 1] = temp
    onChange(newValue)
    setEditingIndex(index - 1)
  }

  const handleMoveDown = (index: number) => {
    if (index === value.length - 1) return
    const newValue = [...value]
    const temp = newValue[index]!
    newValue[index] = newValue[index + 1]!
    newValue[index + 1] = temp
    onChange(newValue)
    setEditingIndex(index + 1)
  }

  const handleFieldChange = (index: number, fieldKey: string, fieldValue: unknown) => {
    const newValue = [...value]
    newValue[index] = { ...newValue[index], [fieldKey]: fieldValue }
    onChange(newValue)
  }

  const renderFieldInput = (item: Record<string, unknown>, field: StructuredListField, index: number) => {
    const fieldValue = item[field.key]

    if (field.type === 'boolean') {
      return (
        <input
          type="checkbox"
          checked={Boolean(fieldValue)}
          onChange={e => handleFieldChange(index, field.key, e.target.checked)}
          disabled={disabled}
          className="w-4 h-4"
        />
      )
    }

    if (field.type === 'number') {
      return (
        <input
          type="number"
          value={Number(fieldValue || 0)}
          onChange={e => handleFieldChange(index, field.key, Number(e.target.value))}
          disabled={disabled}
          className="px-2 py-1 text-sm border border-gray-300 rounded"
        />
      )
    }

    return (
      <input
        type="text"
        value={String(fieldValue || '')}
        onChange={e => handleFieldChange(index, field.key, e.target.value)}
        disabled={disabled}
        className="px-2 py-1 text-sm border border-gray-300 rounded"
      />
    )
  }

  return (
    <div className="space-y-3">
      {value.map((item, index) => (
        <div
          key={index}
          className={`border rounded-lg p-4 ${editingIndex === index ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 bg-white'}`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 space-y-2">
              {schema.map(field => (
                <div key={field.key} className="flex items-center gap-3">
                  <label className="text-sm font-medium text-gray-700 min-w-[120px]">
                    {field.label}
                  </label>
                  {renderFieldInput(item, field, index)}
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-1">
              <button
                type="button"
                onClick={() => handleMoveUp(index)}
                disabled={disabled || index === 0}
                className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronUp className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => handleMoveDown(index)}
                disabled={disabled || index === value.length - 1}
                className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => handleRemove(index)}
                disabled={disabled}
                className="p-1 text-red-500 hover:text-red-700 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={handleAdd}
        disabled={disabled}
        className="w-full py-2 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        <Plus className="w-4 h-4" />
        Hinzufügen
      </button>
    </div>
  )
}
